import InfoIcon from '@mui/icons-material/Info';
import { Box, IconButton, Theme } from '@mui/material';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import React, { useState } from 'react';
import PressureTimelineViewer, {
  PressureTimelineData,
} from '../PressureTimelineViewer/PressureTimelineViewer';

const TITLE = 'Pressure recorded during the test';
const POPOVER_CONTENT = 'Player to read at different speeds the pressure recorded during the test.';

interface StyleProps {
  showingInfo: boolean;
}

const useStyles = makeStyles<Theme, StyleProps>((theme: Theme) => ({
  card: {
    display: 'inline-flex',
    position: 'relative',
    flexDirection: 'column',
    border: '2px solid',
    borderRadius: '10px !important',
    borderColor: ({ showingInfo }) =>
      showingInfo ? theme.palette.primary.main : 'black',
    width: '652px',
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'row',
    padding: '0px 0px 0px 16px !important',
    margin: '40px 0px 0px 0px',
  },
  cardMedia: {
    display: 'flex !important',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  infoIcon: {
    height: 28,
    width: 28,
    color: theme.palette.primary.main,
  },
  expandedInfoIcon: {
    color: 'white',
  },
  popOver: {
    position: 'absolute',
    zIndex: 2,
    top: 0,
    right: 0,
    height: 0,
    width: 0,
    backgroundColor: theme.palette.primary.main,
    borderRadius: '6px',
    transform: 'rotate(0deg)',
    transition: 'all 250ms ease-in-out',
    overflow: 'hidden',
  },
  popOverEntering: {
    height: '100%',
    width: '100%',
    transition: 'all 250ms ease-in-out',
    transform: 'rotate(0deg)',
  },
  popOverContent: {
    display: 'flex',
    flexDirection: 'row',
    margin: '40px 0px 0px 16px',
    color: 'white',
  },
  popOverTitle: {
    marginLeft: theme.spacing(2),
  },
  popOverBody: {
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(4),
    marginRight: theme.spacing(2),
    color: 'white',
  },
  cardTitle: {
    marginLeft: theme.spacing(2),
  },
}));

interface PressureTimelineCardProps {
  data: PressureTimelineData;
}

const PressureTimelineCard: React.FC<PressureTimelineCardProps> = ({ data }) => {
  const [showInfo, setShowInfo] = useState(false);
  const classes = useStyles({ showingInfo: showInfo });

  const CardHeader = () => (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'right',
      }}
    >
      <IconButton
        sx={{ ml: 1, position: 'absolute', zIndex: 3 }}
        onClick={() => setShowInfo(!showInfo)}
      >
        <InfoIcon
          className={clsx(classes.infoIcon, {
            [classes.expandedInfoIcon]: showInfo,
          })}
        />
      </IconButton>
    </Box>
  );

  return (
    <Card className={classes.card}>
      <Box
        className={clsx(classes.popOver, {
          [classes.popOverEntering]: showInfo,
        })}
      >
        <Box className={classes.popOverContent}>
          <Typography className={classes.popOverTitle} variant="h5" component="h2">
            <strong>{TITLE}</strong>
          </Typography>
        </Box>
        <Typography className={classes.popOverBody} variant="body1">
          {POPOVER_CONTENT}
        </Typography>
      </Box>

      {CardHeader()}
      <CardContent className={classes.cardContent}>
        <Typography className={classes.cardTitle} variant="h5" component="h2">
          <strong>{TITLE}</strong>
        </Typography>
      </CardContent>
      <CardMedia className={classes.cardMedia}>
        <PressureTimelineViewer data={data} />
      </CardMedia>
    </Card>
  );
};

export default PressureTimelineCard;
